/**
 * Flight selection modal.
 * Fetches all separator IMEIs and their flights, groups them into a collapsible
 * accordion, and lets the user pick up to 5 flights for live tracking.
 *
 * Committed on "Valider": store is updated + selection cookie renewed.
 * Dismissed on "Annuler": no side effects.
 *
 * @param onClose   - dismiss without committing
 * @param onConfirm - called after the store is updated (Step 4 wires polling + fitToSelection)
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAppStore, getFlightColor } from '../store/appStore';
import { colorForSlot } from '../constants/colors';
import { saveSelection } from '../utils/cookieStore';
import type { Flight, Separator } from '../types/api';
import styles from './SelectionModal.module.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Formats an ISO 8601 UTC timestamp as `DD/MM/YYYY HH:MM`.
 * @param iso - ISO 8601 string
 */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const min = String(d.getUTCMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

/**
 * Returns true if `start_time` is within the rolling date filter window.
 * @param startTime - ISO 8601 UTC flight start
 * @param months    - number of months to look back; 0 means no filter
 */
function passesDateFilter(startTime: string, months: number): boolean {
  if (months === 0) return true;
  const cutoff = new Date();
  cutoff.setUTCMonth(cutoff.getUTCMonth() - months);
  return new Date(startTime) >= cutoff;
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface SeparatorRow {
  separator: Separator;
  flights: Flight[];
  expanded: boolean;
}

const DATE_FILTER_OPTIONS: { label: string; value: number }[] = [
  { label: '1 dernier mois', value: 1 },
  { label: '3 derniers mois', value: 3 },
  { label: '6 derniers mois', value: 6 },
  { label: '12 derniers mois', value: 12 },
  { label: '24 derniers mois', value: 24 },
  { label: 'Pas de limite', value: 0 },
];

// ── Component ─────────────────────────────────────────────────────────────────

interface SelectionModalProps {
  onClose: () => void;
  onConfirm?: () => void;
}

/**
 * Modal for selecting up to 5 flights to track.
 */
export default function SelectionModal({ onClose, onConfirm }: SelectionModalProps) {
  const {
    selectedFlightIds,
    flights: storeFlights,
    selectFlight,
    deselectFlight,
    prefs,
    setPrefs,
  } = useAppStore();

  // ── Local state ─────────────────────────────────────────────────────────────

  const [rows, setRows] = useState<SeparatorRow[]>([]);
  /** All flight objects fetched from API, keyed by id. */
  const [flightMap, setFlightMap] = useState<Record<number, Flight>>({});
  /** Ordered list of pending flight IDs (order determines color slot). */
  const [pendingIds, setPendingIds] = useState<number[]>([...selectedFlightIds]);
  const [dateFilter, setDateFilter] = useState<number>(prefs.flightDateFilterMonths);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Data fetch ───────────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: imeisData } = await axios.get<{ imeis: Separator[] }>('/imeis', {
          params: { type: 'separator' },
        });
        const separators = imeisData.imeis;

        if (cancelled) return;

        // Fetch flights for all separators in parallel
        const flightResults = await Promise.all(
          separators.map((sep) =>
            axios
              .get<{ flights: Flight[] }>(`/imeis/${sep.id}/flights`)
              .then((r) => ({ sepId: sep.id, flights: r.data.flights }))
              .catch(() => ({ sepId: sep.id, flights: [] as Flight[] })),
          ),
        );

        if (cancelled) return;

        // Build flightMap from all fetched flights
        const map: Record<number, Flight> = { ...storeFlights };
        for (const { flights } of flightResults) {
          for (const f of flights) {
            map[f.id] = f;
          }
        }
        setFlightMap(map);

        // Build separator rows — expand rows that have a selected flight
        const flightsBySep = Object.fromEntries(
          flightResults.map(({ sepId, flights }) => [sepId, flights]),
        );
        setRows(
          separators.map((sep) => {
            const sepFlights = flightsBySep[sep.id] ?? [];
            const hasSelected = sepFlights.some((f) =>
              selectedFlightIds.includes(f.id),
            );
            return { separator: sep, flights: sepFlights, expanded: hasSelected };
          }),
        );
      } catch {
        if (!cancelled) setError('Impossible de charger les données. Réessayez.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  /** Toggle a separator row open/closed. */
  const toggleRow = (sepId: number) => {
    setRows((prev) =>
      prev.map((r) =>
        r.separator.id === sepId ? { ...r, expanded: !r.expanded } : r,
      ),
    );
  };

  /** Toggle a flight in the pending selection. */
  const toggleFlight = (flightId: number) => {
    setPendingIds((prev) => {
      if (prev.includes(flightId)) {
        return prev.filter((id) => id !== flightId);
      }
      if (prev.length >= 5) return prev; // limit reached
      return [...prev, flightId];
    });
  };

  /** Apply date filter change: save to prefs cookie and update local state. */
  const handleDateFilterChange = (months: number) => {
    setDateFilter(months);
    setPrefs({ flightDateFilterMonths: months });
  };

  /**
   * Commit the pending selection to the store and cookie.
   * Deselects flights removed from the list; fetches full flight detail for
   * newly added flights (GET /flights/{id} returns lat/lon needed by the map).
   */
  const handleConfirm = async () => {
    // Remove flights no longer in pending
    for (const id of selectedFlightIds) {
      if (!pendingIds.includes(id)) {
        deselectFlight(id);
      }
    }

    // Add new flights in pending order — fetch full details for lat/lon
    for (const id of pendingIds) {
      if (!selectedFlightIds.includes(id)) {
        let flight: Flight | undefined = flightMap[id];
        try {
          const { data } = await axios.get<{ flight: Flight }>(`/flights/${id}`);
          flight = data.flight;
        } catch {
          // Fallback to trimmed object; fitToSelection will skip this flight's coords
        }
        if (flight) selectFlight(id, flight);
      }
    }

    // Save selection to cookie (renews 7-day window) — skipped in tour demo mode
    if (!useAppStore.getState().isTourDemo) {
      saveSelection({
        selectedFlightIds: pendingIds,
        perFlight: Object.fromEntries(
          pendingIds.map((id) => [
            id,
            {
              showNormal: prefs.defaultShowNormal,
              showInstant: prefs.defaultShowInstant,
              showTeams: true,
              isVisible: true,
            },
          ]),
        ),
      });
    }

    onClose();
    onConfirm?.();
  };

  // ── Render helpers ────────────────────────────────────────────────────────────

  /**
   * Returns the color hex for a pending flight, or null if not pending.
   * Already-selected flights keep their real, stable color (via
   * `flightColorIndex`). Flights pending but not yet committed don't have a
   * slot assigned yet, so their color is previewed by simulating pool
   * consumption in `pendingIds` order (matches what `selectFlight` will
   * actually assign on "Valider").
   * @param flightId - flight to look up
   */
  const pendingColor = (flightId: number): string | null => {
    if (!pendingIds.includes(flightId)) return null;

    const { flightColorIndex, availableColorIndices } = useAppStore.getState();
    if (flightColorIndex[flightId] !== undefined) {
      return getFlightColor(flightId);
    }

    const pool = [...availableColorIndices];
    for (const id of pendingIds) {
      if (flightColorIndex[id] !== undefined) continue; // already has a real slot
      if (pool.length === 0) break;
      const idx = pool.shift()!;
      if (id === flightId) return colorForSlot(idx, prefs.flightColors);
    }
    return null;
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()} data-tour="tour-flight-list-panel">

        {/* Header */}
        <div className={styles.header}>
          <span className={styles.title}>Sélection des vols</span>
          <span className={styles.counter}>
            {pendingIds.length}&nbsp;/&nbsp;5&nbsp;sélectionné{pendingIds.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Date filter */}
        <div className={styles.filterBar}>
          <span className={styles.filterLabel}>Afficher les vols des&nbsp;:</span>
          <select
            className={styles.filterSelect}
            value={dateFilter}
            onChange={(e) => handleDateFilterChange(Number(e.target.value))}
          >
            {DATE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Separator/flight list */}
        <div className={`${styles.list} scrollable`}>
          {loading && (
            <p className={styles.status}>Chargement…</p>
          )}
          {error && (
            <p className={styles.statusError}>{error}</p>
          )}

          {!loading && !error && rows.length === 0 && (
            <p className={styles.status}>Aucun séparateur disponible.</p>
          )}

          {!loading && !error && rows.map(({ separator, flights, expanded }) => {
            const filteredFlights = flights.filter((f) =>
              passesDateFilter(f.start_time, dateFilter),
            );

            return (
              <div key={separator.id} className={styles.sepGroup}>
                {/* Separator header row */}
                <button
                  className={styles.sepHeader}
                  onClick={() => toggleRow(separator.id)}
                >
                  <span className={styles.sepArrow}>
                    {expanded ? '▼' : '▶'}
                  </span>
                  <span className={styles.sepName}>{separator.name}</span>
                  {separator.active_flight && (
                    <span className={styles.activeBadge}>● VOL EN COURS</span>
                  )}
                </button>

                {/* Flight rows */}
                {expanded && filteredFlights.length === 0 && (
                  <p className={styles.noFlights}>Aucun vol dans cette période.</p>
                )}

                {expanded && filteredFlights.map((flight) => {
                  const isPending = pendingIds.includes(flight.id);
                  const isAtLimit = !isPending && pendingIds.length >= 5;
                  const color = pendingColor(flight.id);

                  return (
                    <label
                      key={flight.id}
                      className={`${styles.flightRow} ${isAtLimit ? styles.flightRowDisabled : ''}`}
                    >
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={isPending}
                        disabled={isAtLimit}
                        onChange={() => toggleFlight(flight.id)}
                      />
                      <span className={styles.flightLabel}>
                        Vol&nbsp;#{flight.id}
                      </span>
                      <span className={styles.flightDates}>
                        {formatDateTime(flight.start_time)}&nbsp;→&nbsp;
                        {flight.end_time
                          ? formatDateTime(flight.end_time)
                          : <em>en cours</em>}
                      </span>
                      {color && (
                        <span
                          className={styles.colorDot}
                          style={{ background: color }}
                          title={color}
                        />
                      )}
                    </label>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Footer actions */}
        <div className={styles.footer}>
          <button
            className={styles.btnCancel}
            onClick={onClose}
            data-tour="tour-flight-list-panel-close"
          >
            Annuler
          </button>
          <button className={styles.btnConfirm} onClick={handleConfirm}>
            Valider&nbsp;→
          </button>
        </div>
      </div>
    </div>
  );
}
